import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box } from '@mui/material';
import { MapPin, Crosshair } from 'lucide-react';

// Fix for default marker icon issue with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/marker-icon-2x.png',
  iconUrl: '/marker-icon.png',
  shadowUrl: '/marker-shadow.png',
});

interface InteractiveLocationMapProps {
  initialLatitude?: number;
  initialLongitude?: number;
  onLocationSelect: (latitude: number, longitude: number) => void;
  height?: number | string;
  zoom?: number;
  isRTL?: boolean;
}

// Component to handle map clicks
const LocationMarker: React.FC<{
  position: L.LatLngExpression | null;
  onLocationSelect: (lat: number, lng: number) => void;
}> = ({ position, onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return position ? <Marker position={position} /> : null;
};

const InteractiveLocationMap: React.FC<InteractiveLocationMapProps> = ({
  initialLatitude,
  initialLongitude,
  onLocationSelect,
  height = 400,
  zoom = 15,
  isRTL = false,
}) => {
  const [position, setPosition] = useState<L.LatLngExpression | null>(
    initialLatitude && initialLongitude ? [initialLatitude, initialLongitude] : null
  );
  const [mapCenter, setMapCenter] = useState<L.LatLngExpression>([
    initialLatitude || 24.7136, // Default to Riyadh, Saudi Arabia
    initialLongitude || 46.6753,
  ]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const handleLocationSelect = useCallback(
    (lat: number, lng: number) => {
      const newPosition: L.LatLngExpression = [lat, lng];
      setPosition(newPosition);
      onLocationSelect(lat, lng);
    },
    [onLocationSelect]
  );

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert(isRTL ? 'المتصفح لا يدعم تحديد الموقع' : 'Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newPosition: L.LatLngExpression = [lat, lng];
        setPosition(newPosition);
        setMapCenter(newPosition);
        onLocationSelect(lat, lng);
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert(
          isRTL
            ? 'فشل في الحصول على الموقع. يرجى التأكد من تفعيل خدمات الموقع'
            : 'Failed to get your location. Please ensure location services are enabled.'
        );
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [onLocationSelect, isRTL]);

  return (
    <div className="space-y-3">
      {/* Instructions and Current Location Button */}
      <div className="flex items-start justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-2 flex-1">
          <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">
              {isRTL ? 'كيفية تحديد الموقع:' : 'How to select location:'}
            </p>
            <p className="text-xs text-blue-800">
              {isRTL
                ? 'انقر على الخريطة لتحديد الموقع، أو استخدم زر "موقعي الحالي" للحصول على موقعك الحالي تلقائياً'
                : 'Click anywhere on the map to set the location, or use "Current Location" button to get your current position automatically'}
            </p>
          </div>
        </div>
        <button
          onClick={handleGetCurrentLocation}
          disabled={isGettingLocation}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap
            ${
              isGettingLocation
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
        >
          <Crosshair className={`w-4 h-4 ${isGettingLocation ? 'animate-spin' : ''}`} />
          {isGettingLocation
            ? isRTL
              ? 'جاري التحديد...'
              : 'Getting...'
            : isRTL
            ? 'موقعي الحالي'
            : 'Current Location'}
        </button>
      </div>

      {/* Selected Location Info */}
      {position && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <p className="text-sm font-semibold text-emerald-900">
            {isRTL ? 'الإحداثيات المحددة:' : 'Selected Coordinates:'}
          </p>
          <p className="text-sm text-emerald-800 font-mono mt-1">
            {(position as [number, number])[0].toFixed(6)}, {(position as [number, number])[1].toFixed(6)}
          </p>
        </div>
      )}

      {/* Interactive Map */}
      <Box
        sx={{
          height,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: position ? 'rgb(16, 185, 129)' : 'rgb(209, 213, 219)',
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            borderRadius: 2,
            cursor: 'crosshair',
          },
          '& .leaflet-popup-content-wrapper': {
            borderRadius: '8px',
          },
          '& .leaflet-control-zoom': {
            border: 'none',
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .leaflet-control-attribution': {
            fontSize: '0.7rem',
          },
        }}
      >
        <MapContainer
          center={mapCenter}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          dragging={true}
          touchZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker position={position} onLocationSelect={handleLocationSelect} />
        </MapContainer>
      </Box>
    </div>
  );
};

export default InteractiveLocationMap;
